declare const Janus: any;

export interface InitJanusPublisherParams {
  janusServer: string;
  janusSdkServer: string;
  sdkServerUserInfo: any;
  suid: string;
  frequencyHz?: number;
  onError?: (err: any) => void;
  onPublish?: (handle: any) => void;
}

// マイク + トーン を合成した MediaStream を生成
async function createInjectedAudioStream(frequencyHz: number): Promise<MediaStream> {
  const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const audioCtx = new AudioContext();

  const micSource = audioCtx.createMediaStreamSource(micStream);

  const oscillator = audioCtx.createOscillator();
  oscillator.frequency.value = frequencyHz;

  const gain = audioCtx.createGain();
  gain.gain.value = 0;

  oscillator.connect(gain);

  const destination = audioCtx.createMediaStreamDestination();
  micSource.connect(destination);
  gain.connect(destination);

  oscillator.start();

  let isOn = false;
  setInterval(() => {
    isOn = !isOn;
    gain.gain.value = isOn ? 0.05 : 0;
  }, 3000);

  return destination.stream;
}

export function initJanusPublisher({
  janusServer,
  janusSdkServer,
  sdkServerUserInfo,
  suid,
  frequencyHz,
  onError,
  onPublish,
}: InitJanusPublisherParams): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const form = new URLSearchParams();
    form.append("credential", sdkServerUserInfo.credential);

    fetch(`${janusSdkServer}/responder`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
    })
      .then((res) => res.json())
      .then(async (response) => {
        const turn = {
          urls: response.data.turn_url,
          username: response.data.turn_user,
          credential: response.data.turn_credential,
        };

        const token = response.data.token;

        const roomList = response.data.room_list;
        const room = roomList.find((r: any) =>
          r.transmitters?.some((t: any) => t.suid === suid)
        );
        if (!room) {
          throw new Error(`SUID:${suid} に対応する room が見つかりません`);
        }
        const roomId = room.room_num;

        const stream = frequencyHz
          ? await createInjectedAudioStream(frequencyHz)
          : await navigator.mediaDevices.getUserMedia({ audio: true });

        Janus.init({
          debug: "all",
          callback: () => {
            const janus = new Janus({
              server: janusServer,
              iceServers: [turn],
              token: token,
              success: () => {
                let pluginHandle: any;

                janus.attach({
                  plugin: "janus.plugin.soliton.videoroom",
                  success: (handle: any) => {
                    pluginHandle = handle;

                    pluginHandle.send({
                      message: {
                        request: "join",
                        room: roomId,
                        ptype: "publisher",
                        display: "myuser",
                      },
                    });

                    pluginHandle.createOffer({
                      media: {
                        audio: true,
                        video: false,
                        audioSend: true,
                        videoSend: false,
                        audioRecv: false,
                        videoRecv: false,
                      },
                      stream,
                      success: (jsep: any) => {
                        pluginHandle.send({
                          message: { request: "configure", audio: true },
                          jsep,
                        });

                        pluginHandle.onicecandidate = (candidate: any) => {
                          if (candidate) {
                            console.log("ICE candidate:", candidate);
                            pluginHandle.send({
                              message: { request: "trickle" },
                              candidate,
                            });
                          }
                        };

                        onPublish?.(pluginHandle);
                        resolve();
                      },
                      error: (err: any) => {
                        onError?.(err);
                        reject(err);
                      },
                    });
                  },
                  onmessage: (_msg: any, jsep: any) => {
                    if (jsep) {
                      try {
                        if (jsep.type && jsep.sdp) {
                          console.log("Handling remote JSEP:", jsep);
                          pluginHandle.handleRemoteJsep({ jsep });
                        } else {
                          console.error("Invalid JSEP received:", jsep);
                        }
                      } catch (error) {
                        console.error("Error handling remote JSEP:", error);
                        onError?.(error);
                      }
                    }
                  },
                  error: (err: any) => {
                    onError?.(err);
                    reject(err);
                  },
                  oncleanup: () => {
                    console.log("Cleanup triggered");
                  },
                });
              },
              error: (err: any) => {
                onError?.(err);
                reject(err);
              },
              destroyed: () => {
                console.log("Janus session destroyed");
              },
            });
          },
        });
      })
      .catch((err) => {
        onError?.(err);
        reject(err);
      });
  });
}
